/**
 * Safe localStorage wrapper that handles quota exceeded errors
 * and provides fallback mechanisms
 */

import { useState } from "react";

class SafeLocalStorage {
    private memoryStorage: Map<string, unknown> = new Map();

    /**
     * Get item from localStorage with fallback to memory
     */
    getItem(key: string): string | null {
        try {
            const item = localStorage.getItem(key);
            if (item !== null) return item;
        } catch (error) {
            console.warn(`Failed to get ${key} from localStorage:`, error);
        }

        // Fallback to memory storage
        const memoryValue = this.memoryStorage.get(key);
        return memoryValue !== undefined ? JSON.stringify(memoryValue) : null;
    }

    /**
     * Set item in localStorage with automatic cleanup and fallback
     */
    setItem(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
            // Remove from memory storage if successfully saved to localStorage
            this.memoryStorage.delete(key);
        } catch (error) {
            if (error instanceof DOMException && error.name === "QuotaExceededError") {
                console.warn("localStorage quota exceeded, attempting automatic cleanup...");

                // Aggressive cleanup - remove more items
                this.aggressiveCleanup();

                // Try again after cleanup
                try {
                    localStorage.setItem(key, value);
                    this.memoryStorage.delete(key);
                    // Successfully saved after automatic cleanup
                } catch (_retryError) {
                    console.warn(
                        `Failed to save ${key} to localStorage after cleanup, using memory fallback`,
                    );
                    // Fall back to memory storage
                    this.memoryStorage.set(key, JSON.parse(value));
                }
            } else {
                console.error("localStorage error:", error);
                // Fall back to memory storage
                this.memoryStorage.set(key, JSON.parse(value));
            }
        }
    }

    /**
     * Remove item from both localStorage and memory
     */
    removeItem(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.warn(`Failed to remove ${key} from localStorage:`, error);
        }
        this.memoryStorage.delete(key);
    }

    /**
     * Clear all storage
     */
    clear(): void {
        try {
            localStorage.clear();
        } catch (error) {
            console.warn("Failed to clear localStorage:", error);
        }
        this.memoryStorage.clear();
    }

    /**
     * Get estimated storage usage
     */
    getStorageInfo(): { used: number; quota: number; percentage: number } {
        let used = 0;
        const quota = 5 * 1024 * 1024; // Default 5MB quota

        try {
            // Calculate used space
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key) {
                    used += key.length + (localStorage.getItem(key)?.length || 0);
                }
            }
        } catch (error) {
            console.warn("Failed to calculate storage usage:", error);
        }

        // Add memory storage size
        this.memoryStorage.forEach((value, key) => {
            used += key.length + JSON.stringify(value).length;
        });

        return {
            used,
            quota,
            percentage: (used / quota) * 100,
        };
    }

    /**
     * Check if a key is critical and should not be removed during cleanup.
     * This is especially important for MSAL tokens to prevent session loss.
     */
    private isCriticalKey(key: string): boolean {
        const criticalKeySubstrings = [
            "msal", // Authentication tokens and state
            "auth", // Authentication
            "login.windows.net", // Microsoft SSO tokens
            "login.microsoftonline.com", // Microsoft SSO tokens
            "user", // User data
            "settings", // User settings
            "selectedOrganization", // Current selection
            "features", // Feature flags
            "lastLoginTime", // Login tracking
            "termsOfUse_accepted", // Terms of use acceptance
        ];

        const lowerKey = key.toLowerCase();

        // Check substring matches
        if (criticalKeySubstrings.some((critical) => lowerKey.includes(critical.toLowerCase()))) {
            return true;
        }

        // MSAL stores tokens with keys like: "{homeAccountId}-{environment}-{credentialType}-..."
        // These contain UUIDs and tenant IDs, e.g.: "abc123-def456.login.microsoftonline.com-accesstoken-..."
        // Protect any key that looks like an MSAL token cache entry
        if (
            key.includes("-accesstoken-") ||
            key.includes("-idtoken-") ||
            key.includes("-refreshtoken-")
        ) {
            return true;
        }

        return false;
    }

    /**
     * Aggressive cleanup that removes more data
     */
    public aggressiveCleanup(): void {
        // Starting aggressive localStorage cleanup

        const keysToRemove: string[] = [];
        const itemInfo: Array<{ key: string; size: number }> = [];

        try {
            // Collect all non-critical items
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && !this.isCriticalKey(key)) {
                    const value = localStorage.getItem(key) || "";
                    itemInfo.push({
                        key,
                        size: key.length + value.length,
                    });
                }
            }

            // Sort by size (largest first)
            itemInfo.sort((a, b) => b.size - a.size);

            // Remove largest items first
            let removed = 0;
            for (const item of itemInfo) {
                try {
                    localStorage.removeItem(item.key);
                    keysToRemove.push(item.key);
                    removed += item.size;

                    // Remove at least 1MB of data
                    if (removed > 1024 * 1024) {
                        break;
                    }
                } catch (e) {
                    console.warn(`Failed to remove ${item.key}:`, e);
                }
            }

            // Aggressive cleanup complete
        } catch (error) {
            console.error("Error during aggressive cleanup:", error);

            // Last resort - clear everything except critical items
            const criticalData: Record<string, string> = {};

            // Save critical items
            for (let i = localStorage.length - 1; i >= 0; i--) {
                const key = localStorage.key(i);
                if (key && this.isCriticalKey(key)) {
                    criticalData[key] = localStorage.getItem(key) || "";
                }
            }

            // Clear everything
            localStorage.clear();

            // Restore critical items
            for (const [key, value] of Object.entries(criticalData)) {
                try {
                    localStorage.setItem(key, value);
                } catch (e) {
                    console.error(`Failed to restore critical key ${key}:`, e);
                }
            }

            // Performed emergency cleanup - cleared all non-critical data
        }
    }
}

// Export singleton instance
export const safeLocalStorage = new SafeLocalStorage();

// Export hook for React components
export function useSafeLocalStorage<T>(
    key: string,
    initialValue: T,
): [T, (value: T | ((val: T) => T)) => void] {
    // Get initial value
    const getStoredValue = (): T => {
        try {
            const item = safeLocalStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch {
            return initialValue;
        }
    };

    const [storedValue, setStoredValue] = useState<T>(getStoredValue);

    // Update function that handles localStorage
    const setValue = (value: T | ((val: T) => T)) => {
        try {
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            safeLocalStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (error) {
            console.error(`Error setting ${key}:`, error);
        }
    };

    return [storedValue, setValue];
}

// Export storage management functions
export function getStorageStatus() {
    const info = safeLocalStorage.getStorageInfo();
    return {
        ...info,
        formattedUsed: `${(info.used / 1024 / 1024).toFixed(2)} MB`,
        formattedQuota: `${(info.quota / 1024 / 1024).toFixed(2)} MB`,
        isFull: info.percentage >= 90,
    };
}

export function cleanupStorage() {
    const beforeInfo = safeLocalStorage.getStorageInfo();

    // Force cleanup
    safeLocalStorage.aggressiveCleanup();

    const afterInfo = safeLocalStorage.getStorageInfo();
    const freedSpace = beforeInfo.used - afterInfo.used;
    return {
        freedSpace,
        beforeUsage: beforeInfo.percentage,
        afterUsage: afterInfo.percentage,
    };
}
